import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { MatDialogCustomComponent } from '../../mat-dialog-custom/mat-dialog-custom.component';
import { ColombiaNITValidator } from './nit-validator.helper';
import { ChileRUTValidator } from './rut-validator.helper';

export type CreateCommissionProfilesDto = {
  companyAddress: string;
  country: string;
  province?: string;
  city: string;
  postalCode: string;
  phoneNumber: string;
  profileName: string;
  taxId?: string;
};

@Component({
  selector: 'ew-commissions-create-profile',
  templateUrl: './create-profile.component.html',
  styleUrl: './create-profile.component.scss',
})
export class CommissionCreateProfileComponent {
  loading: boolean = false;
  provinces: any[] = [];
  unlockSend: boolean = true;

  profile = new FormGroup({
    address: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    province: new FormControl(null),
    city: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [
      Validators.required,
      Validators.maxLength(10),
    ]),
    phoneNumber: new FormControl('', [Validators.required]),
    profileName: new FormControl('', [Validators.required]),
    taxId: new FormControl(''),
  });

  constructor(
    private readonly API: ApiService,
    private readonly DIALOG: MatDialogRef<MatDialogCustomComponent>,
  ) {}

  /**
   * Check if the input province should be shown based on the country selected by the user
   */
  get showInputProvince(): boolean {
    const country = this.profile.value.country;
    return country === 'ca' || country === 'us' || country === 'au';
  }

  /**
   * Check if the input taxId should be shown and update its validators
   * @author Otávio Rodrigues.
   */
  private updateProvinceValidators(): void {
    if (this.showInputProvince) {
      this.getProvinces();
      this.profile.get('province')?.setValidators([Validators.required]);
    } else {
      this.profile.get('province')?.setValidators([]);
    }
    this.profile.controls['province'].updateValueAndValidity();
  }

  /**
   * Check if the input taxId should be shown and update its validators
   * @author Otávio Rodrigues.
   */
  private updateTaxIdValidators(): void {
    if (this.profile.value.country === 'br') {
      this.profile.get('taxId')?.setValidators([Validators.required]);
      this.profile.controls['taxId'].updateValueAndValidity();
    }

    if (this.profile.value.country === 'co') {
      this.profile.get('taxId')?.setValidators([Validators.required]);
      this.profile.controls['taxId'].updateValueAndValidity();

      const taxId = this.profile.get('taxId')?.value;
      const isValid = ColombiaNITValidator.validate(taxId!);

      if (!isValid) {
        this.profile.get('taxId')?.setErrors(['NIT not valid!']);
      }
    }

    if (this.profile.value.country === 'cl') {
      this.profile.get('taxId')?.setValidators([Validators.required]);
      this.profile.controls['taxId'].updateValueAndValidity();
    }
  }

  /**
   * Check what inputs should shown based on country and update its validators
   * @author Otávio Rodrigues.
   */
  onCountryChange(): void {
    if (this.profile.value.country) {
      this.profile.patchValue({ province: this.profile.value.province });
    }

    this.getProvinces();
    this.updateProvinceValidators();
    this.updateTaxIdValidators();
  }

  /**
   * Get the provinces based on the country selected by the user
   * @author Otávio Rodrigues.
   */
  getProvinces(): void {
    if (['us', 'ca', 'au'].includes(this.profile?.value.country!)) {
      this.API.get(
        `v1/public/payment-requests/provinces/${this.profile.value.country}/`,
      ).subscribe((data: any) => {
        this.provinces = data.records;
        this.profile.controls['province'].setValidators(
          this.showInputProvince ? [Validators.required] : null,
        );
        this.profile.controls['province'].updateValueAndValidity();
      });
    }
  }

  /**
   * Submit the form to create a new commission profile
   */
  submit(): void {
    if (!this.isCustomValidatorsValid()) {
      return;
    }

    this.loading = true;
    this.unlockSend = false;

    this.API.post<CreateCommissionProfilesDto>(`/v1/commissions/profile`, {
      companyAddress: this.profile?.value.address!,
      country: this.profile?.value.country!,
      province: this.profile?.value.province!,
      city: this.profile?.value.city!,
      postalCode: this.profile?.value.postalCode!,
      phoneNumber: this.profile?.value.phoneNumber!,
      profileName: this.profile?.value.profileName!,
      taxId: this.profile?.value.taxId!,
    }).subscribe(
      (data: any) => {
        this.API.alertService.success('commissions:profileCreatedSuccess');
        this.loading = false;

        setTimeout(() => {
          this.DIALOG.close();
          this.DIALOG.afterClosed().subscribe(() => {
            window.location.reload();
          });
        }, 3_000);
      },
      (err) => {
        this.API.alertService.error(
          `commissions:profileCreatedFailed - ${err.statusText}`,
        ),
          ((this.loading = false), (this.unlockSend = true));
      },
    );
  }

  validations(): boolean {
    return this.profile.valid && this.unlockSend;
  }

  private isCustomValidatorsValid(): boolean {
    let isValid = true;

    const taxIdControl = this.profile.get('taxId');
    if (taxIdControl && this.profile.value.country === 'co') {
      if (!ColombiaNITValidator.validate(this.profile?.value.taxId)) {
        taxIdControl.setErrors({ invalidNIT: true });
        isValid = false;
      } else {
        taxIdControl.setErrors(null);
      }
      taxIdControl.markAsTouched();
    } else if (taxIdControl && this.profile.value.country === 'cl') {
      if (!ChileRUTValidator.validate(this.profile?.value.taxId)) {
        taxIdControl.setErrors({ invalidRUT: true });
        isValid = false;
      } else {
        taxIdControl.setErrors(null);
      }
      taxIdControl.markAsTouched();
    }

    return isValid;
  }
}
